'use client';
function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_without_holes(arr) {
    if (Array.isArray(arr)) return _array_like_to_array(arr);
}
function _iterable_to_array(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _non_iterable_spread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _to_consumable_array(arr) {
    return _array_without_holes(arr) || _iterable_to_array(arr) || _unsupported_iterable_to_array(arr) || _non_iterable_spread();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _excluded = [
    "selected",
    "rowId",
    "row",
    "index",
    "style",
    "rowHeight",
    "className",
    "visibleColumns",
    "pinnedColumns",
    "offsetLeft",
    "columnsTotalWidth",
    "firstColumnIndex",
    "lastColumnIndex",
    "focusedColumnIndex",
    "isFirstVisible",
    "isLastVisible",
    "isNotVisible",
    "showBottomBorder",
    "scrollbarWidth",
    "gridHasFiller",
    "onClick",
    "onDoubleClick",
    "onMouseEnter",
    "onMouseLeave",
    "onMouseOut",
    "onMouseOver"
];
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { unstable_useForkRef as useForkRef } from '@mui/utils';
import { fastMemo } from '@mui/x-internals/fastMemo';
import { forwardRef } from '@mui/x-internals/forwardRef';
import { isObjectEmpty } from '@mui/x-internals/isObjectEmpty';
import { GridEditModes, GridCellModes } from "../models/gridEditRowModel.js";
import { gridClasses } from "../constants/gridClasses.js";
import { composeGridClasses } from "../utils/composeGridClasses.js";
import { useGridRootProps } from "../hooks/utils/useGridRootProps.js";
import { shouldCellShowLeftBorder, shouldCellShowRightBorder } from "../utils/cellBorderUtils.js";
import { gridColumnPositionsSelector } from "../hooks/features/columns/gridColumnsSelector.js";
import { useGridSelector, objectShallowCompare, useGridSelectorV8 } from "../hooks/utils/useGridSelector.js";
import { useGridVisibleRows } from "../hooks/utils/useGridVisibleRows.js";
import { findParentElementFromClassName, isEventTargetInPortal } from "../utils/domUtils.js";
import { GRID_CHECKBOX_SELECTION_COL_DEF } from "../colDef/gridCheckboxSelectionColDef.js";
import { GRID_ACTIONS_COLUMN_TYPE } from "../colDef/gridActionsColDef.js";
import { GRID_DETAIL_PANEL_TOGGLE_FIELD, PinnedColumnPosition } from "../internals/constants.js";
import { gridSortModelSelector } from "../hooks/features/sorting/gridSortingSelector.js";
import { gridRowMaximumTreeDepthSelector } from "../hooks/features/rows/gridRowsSelector.js";
import { gridEditRowsStateSelector, gridRowIsEditingSelector } from "../hooks/features/editing/gridEditingSelectors.js";
import { GridScrollbarFillerCell as ScrollbarFiller } from "./GridScrollbarFillerCell.js";
import { getPinnedCellOffset } from "../internals/utils/getPinnedCellOffset.js";
import { useGridConfiguration } from "../hooks/utils/useGridConfiguration.js";
import { useGridPrivateApiContext } from "../hooks/utils/useGridPrivateApiContext.js";
import { createSelectorV8 } from "../utils/createSelector.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var isRowReorderingEnabledSelector = createSelectorV8(gridEditRowsStateSelector, function(editRows, rowReordering) {
    if (!rowReordering) {
        return false;
    }
    var isEditingRows = !isObjectEmpty(editRows);
    return !isEditingRows;
});
var GridRow = forwardRef(function GridRow(props, refProp) {
    var selected = props.selected, rowId = props.rowId, row = props.row, index = props.index, styleProp = props.style, rowHeight = props.rowHeight, className = props.className, visibleColumns = props.visibleColumns, pinnedColumns = props.pinnedColumns, offsetLeft = props.offsetLeft, columnsTotalWidth = props.columnsTotalWidth, firstColumnIndex = props.firstColumnIndex, lastColumnIndex = props.lastColumnIndex, focusedColumnIndex = props.focusedColumnIndex, isFirstVisible = props.isFirstVisible, isLastVisible = props.isLastVisible, isNotVisible = props.isNotVisible, showBottomBorder = props.showBottomBorder, scrollbarWidth = props.scrollbarWidth, gridHasFiller = props.gridHasFiller, onClick = props.onClick, onDoubleClick = props.onDoubleClick, onMouseEnter = props.onMouseEnter, onMouseLeave = props.onMouseLeave, onMouseOut = props.onMouseOut, onMouseOver = props.onMouseOver, other = _objectWithoutPropertiesLoose(props, _excluded);
    var apiRef = useGridPrivateApiContext();
    var configuration = useGridConfiguration();
    var ref = React.useRef(null);
    var rootProps = useGridRootProps();
    var currentPage = useGridVisibleRows(apiRef, rootProps);
    var sortModel = useGridSelector(apiRef, gridSortModelSelector);
    var treeDepth = useGridSelector(apiRef, gridRowMaximumTreeDepthSelector);
    var columnPositions = useGridSelector(apiRef, gridColumnPositionsSelector);
    var rowReordering = rootProps.rowReordering;
    var isRowReorderingEnabled = useGridSelectorV8(apiRef, isRowReorderingEnabledSelector, rowReordering);
    var handleRef = useForkRef(ref, refProp);
    var rowNode = apiRef.current.getRowNode(rowId);
    var editing = useGridSelectorV8(apiRef, gridRowIsEditingSelector, {
        rowId: rowId,
        editMode: rootProps.editMode
    });
    var editable = rootProps.editMode === GridEditModes.Row;
    var hasFocusCell = focusedColumnIndex !== undefined;
    var hasVirtualFocusCellLeft = hasFocusCell && focusedColumnIndex >= pinnedColumns.left.length && focusedColumnIndex < firstColumnIndex;
    var hasVirtualFocusCellRight = hasFocusCell && focusedColumnIndex < visibleColumns.length - pinnedColumns.right.length && focusedColumnIndex >= lastColumnIndex;
    var classes = composeGridClasses(rootProps.classes, {
        root: [
            'row',
            selected && 'selected',
            editable && 'row--editable',
            editing && 'row--editing',
            isFirstVisible && 'row--firstVisible',
            isLastVisible && 'row--lastVisible',
            showBottomBorder && 'row--borderBottom',
            rowHeight === 'auto' && 'row--dynamicHeight'
        ]
    });
    var getRowAriaAttributes = configuration.hooks.useGridRowAriaAttributes();
    React.useLayoutEffect(function() {
        if (currentPage.range) {
            var rowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(rowId);
            // Pinned rows are not part of the visible rows
            if (rowIndex !== undefined) {
                apiRef.current.unstable_setLastMeasuredRowIndex(rowIndex);
            }
        }
        if (ref.current && rowHeight === 'auto') {
            return apiRef.current.observeRowHeight(ref.current, rowId);
        }
        return undefined;
    }, [
        apiRef,
        currentPage.range,
        rowHeight,
        rowId
    ]);
    var publish = React.useCallback(function(eventName, propHandler) {
        return function(event) {
            // Ignore portal
            if (isEventTargetInPortal(event)) {
                return;
            }
            // The row might have been deleted
            if (!apiRef.current.getRow(rowId)) {
                return;
            }
            apiRef.current.publishEvent(eventName, apiRef.current.getRowParams(rowId), event);
            if (propHandler) {
                propHandler(event);
            }
        };
    }, [
        apiRef,
        rowId
    ]);
    var publishClick = React.useCallback(function(event) {
        var cell = findParentElementFromClassName(event.target, gridClasses.cell);
        var field = cell === null || cell === void 0 ? void 0 : cell.getAttribute('data-field');
        // Check if the field is available because the cell that fills the empty
        // space of the row has no field.
        if (field) {
            // User clicked in the checkbox added by checkboxSelection
            if (field === GRID_CHECKBOX_SELECTION_COL_DEF.field) {
                return;
            }
            // User opened a detail panel
            if (field === GRID_DETAIL_PANEL_TOGGLE_FIELD) {
                return;
            }
            // User reorders a row
            if (field === '__reorder__') {
                return;
            }
            // User is editing a cell
            if (apiRef.current.getCellMode(rowId, field) === GridCellModes.Edit) {
                return;
            }
            // User clicked a button from the "actions" column type
            var column = apiRef.current.getColumn(field);
            if ((column === null || column === void 0 ? void 0 : column.type) === GRID_ACTIONS_COLUMN_TYPE) {
                return;
            }
        }
        publish('rowClick', onClick)(event);
    }, [
        apiRef,
        onClick,
        publish,
        rowId
    ]);
    var slots = rootProps.slots, slotProps = rootProps.slotProps, disableColumnReorder = rootProps.disableColumnReorder;
    var heightEntry = useGridSelector(apiRef, function() {
        return _extends({}, apiRef.current.getRowHeightEntry(rowId));
    }, objectShallowCompare);
    var style = React.useMemo(function() {
        if (isNotVisible) {
            return {
                opacity: 0,
                width: 0,
                height: 0
            };
        }
        var rowStyle = _extends({}, styleProp, {
            maxHeight: rowHeight === 'auto' ? 'none' : rowHeight,
            // max-height doesn't support "auto"
            minHeight: rowHeight,
            '--height': typeof rowHeight === 'number' ? "".concat(rowHeight, "px") : rowHeight
        });
        if (heightEntry.spacingTop) {
            var property = rootProps.rowSpacingType === 'border' ? 'borderTopWidth' : 'marginTop';
            rowStyle[property] = heightEntry.spacingTop;
        }
        if (heightEntry.spacingBottom) {
            var property1 = rootProps.rowSpacingType === 'border' ? 'borderBottomWidth' : 'marginBottom';
            var propertyValue = rowStyle[property1];
            // avoid overriding existing value
            if (typeof propertyValue !== 'number') {
                propertyValue = parseInt(propertyValue || '0', 10);
            }
            propertyValue += heightEntry.spacingBottom;
            rowStyle[property1] = propertyValue;
        }
        return rowStyle;
    }, [
        isNotVisible,
        rowHeight,
        styleProp,
        heightEntry,
        rootProps.rowSpacingType
    ]);
    var rowClassNames = apiRef.current.unstable_applyPipeProcessors('rowClassName', [], rowId);
    var ariaAttributes = getRowAriaAttributes(rowNode, index);
    if (typeof rootProps.getRowClassName === 'function') {
        var _currentPage_range;
        var indexRelativeToCurrentPage = index - (((_currentPage_range = currentPage.range) === null || _currentPage_range === void 0 ? void 0 : _currentPage_range.firstRowIndex) || 0);
        var rowParams = _extends({}, apiRef.current.getRowParams(rowId), {
            isFirstVisible: indexRelativeToCurrentPage === 0,
            isLastVisible: indexRelativeToCurrentPage === currentPage.rows.length - 1,
            indexRelativeToCurrentPage: indexRelativeToCurrentPage
        });
        rowClassNames.push(rootProps.getRowClassName(rowParams));
    }
    var getCell = function(column, indexInSection, indexRelativeToAllColumns, sectionLength) {
        var pinnedPosition = arguments.length > 4 && arguments[4] !== void 0 ? arguments[4] : PinnedColumnPosition.NONE;
        var cellColSpanInfo = apiRef.current.unstable_getCellColSpanInfo(rowId, indexRelativeToAllColumns);
        if (cellColSpanInfo === null || cellColSpanInfo === void 0 ? void 0 : cellColSpanInfo.spannedByColSpan) {
            return null;
        }
        var _cellColSpanInfo_cellProps_width;
        var width = (_cellColSpanInfo_cellProps_width = cellColSpanInfo === null || cellColSpanInfo === void 0 ? void 0 : cellColSpanInfo.cellProps.width) !== null && _cellColSpanInfo_cellProps_width !== void 0 ? _cellColSpanInfo_cellProps_width : column.computedWidth;
        var _cellColSpanInfo_cellProps_colSpan;
        var colSpan = (_cellColSpanInfo_cellProps_colSpan = cellColSpanInfo === null || cellColSpanInfo === void 0 ? void 0 : cellColSpanInfo.cellProps.colSpan) !== null && _cellColSpanInfo_cellProps_colSpan !== void 0 ? _cellColSpanInfo_cellProps_colSpan : 1;
        var pinnedOffset = getPinnedCellOffset(pinnedPosition, column.computedWidth, indexRelativeToAllColumns, columnPositions, columnsTotalWidth, scrollbarWidth);
        if (rowNode.type === 'skeletonRow') {
            return /*#__PURE__*/ _jsx(slots.skeletonCell, {
                type: column.type,
                width: width,
                height: rowHeight,
                field: column.field,
                align: column.align
            }, column.field);
        }
        // when the cell is a reorder cell we are not allowing to reorder the col
        // fixes https://github.com/mui/mui-x/issues/11126
        var isReorderCell = column.field === '__reorder__';
        var canReorderColumn = !(disableColumnReorder || column.disableReorder);
        var canReorderRow = isRowReorderingEnabled && !sortModel.length && treeDepth <= 1;
        var disableDragEvents = !(canReorderColumn || isReorderCell && canReorderRow);
        var cellIsNotVisible = pinnedPosition === PinnedColumnPosition.VIRTUAL;
        var showLeftBorder = shouldCellShowLeftBorder(pinnedPosition, indexInSection);
        var showRightBorder = shouldCellShowRightBorder(pinnedPosition, indexInSection, sectionLength, rootProps.showCellVerticalBorder, gridHasFiller);
        return /*#__PURE__*/ _jsx(slots.cell, _extends({
            column: column,
            width: width,
            rowId: rowId,
            align: column.align || 'left',
            colIndex: indexRelativeToAllColumns,
            colSpan: colSpan,
            disableDragEvents: disableDragEvents,
            isNotVisible: cellIsNotVisible,
            pinnedOffset: pinnedOffset,
            pinnedPosition: pinnedPosition,
            showLeftBorder: showLeftBorder,
            showRightBorder: showRightBorder,
            row: row,
            rowNode: rowNode
        }, slotProps === null || slotProps === void 0 ? void 0 : slotProps.cell), column.field);
    };
    var leftCells = pinnedColumns.left.map(function(column, i) {
        var indexRelativeToAllColumns = i;
        return getCell(column, i, indexRelativeToAllColumns, pinnedColumns.left.length, PinnedColumnPosition.LEFT);
    });
    var rightCells = pinnedColumns.right.map(function(column, i) {
        var indexRelativeToAllColumns = visibleColumns.length - pinnedColumns.right.length + i;
        return getCell(column, i, indexRelativeToAllColumns, pinnedColumns.right.length, PinnedColumnPosition.RIGHT);
    });
    var middleColumnsLength = visibleColumns.length - pinnedColumns.left.length - pinnedColumns.right.length;
    var cells = [];
    if (hasVirtualFocusCellLeft) {
        cells.push(getCell(visibleColumns[focusedColumnIndex], focusedColumnIndex - pinnedColumns.left.length, focusedColumnIndex, middleColumnsLength, PinnedColumnPosition.VIRTUAL));
    }
    for(var i = firstColumnIndex; i < lastColumnIndex; i += 1){
        var column = visibleColumns[i];
        var indexInSection = i - pinnedColumns.left.length;
        if (!column) {
            continue;
        }
        cells.push(getCell(column, indexInSection, i, middleColumnsLength));
    }
    if (hasVirtualFocusCellRight) {
        cells.push(getCell(visibleColumns[focusedColumnIndex], focusedColumnIndex - pinnedColumns.left.length, focusedColumnIndex, middleColumnsLength, PinnedColumnPosition.VIRTUAL));
    }
    var eventHandlers = row ? {
        onClick: publishClick,
        onDoubleClick: publish('rowDoubleClick', onDoubleClick),
        onMouseEnter: publish('rowMouseEnter', onMouseEnter),
        onMouseLeave: publish('rowMouseLeave', onMouseLeave),
        onMouseOut: publish('rowMouseOut', onMouseOut),
        onMouseOver: publish('rowMouseOver', onMouseOver)
    } : null;
    return /*#__PURE__*/ _jsxs("div", _extends({
        "data-id": rowId,
        "data-rowindex": index,
        role: "row",
        className: clsx.apply(void 0, _to_consumable_array(rowClassNames).concat([
            classes.root,
            className
        ])),
        style: style
    }, ariaAttributes, eventHandlers, other, {
        ref: handleRef,
        children: [
            leftCells,
            /*#__PURE__*/ _jsx("div", {
                role: "presentation",
                className: gridClasses.cellOffsetLeft,
                style: {
                    width: offsetLeft
                }
            }),
            cells,
            /*#__PURE__*/ _jsx("div", {
                role: "presentation",
                className: clsx(gridClasses.cell, gridClasses.cellEmpty)
            }),
            rightCells,
            scrollbarWidth !== 0 && /*#__PURE__*/ _jsx(ScrollbarFiller, {
                pinnedRight: pinnedColumns.right.length > 0,
                borderTop: !isFirstVisible
            })
        ]
    }));
});
process.env.NODE_ENV !== "production" ? GridRow.propTypes = {
    // ----------------------------- Warning --------------------------------
    // | These PropTypes are generated from the TypeScript type definitions |
    // | To update them edit the TypeScript types and run "pnpm proptypes"  |
    // ----------------------------------------------------------------------
    columnsTotalWidth: PropTypes.number.isRequired,
    firstColumnIndex: PropTypes.number.isRequired,
    /**
   * Determines which cell has focus.
   * If `null`, no cell in this row has focus.
   */ focusedColumnIndex: PropTypes.number,
    gridHasFiller: PropTypes.bool.isRequired,
    /**
   * Index of the row in the whole sorted and filtered dataset.
   * If some rows above have expanded children, this index also take those children into account.
   */ index: PropTypes.number.isRequired,
    isFirstVisible: PropTypes.bool.isRequired,
    isLastVisible: PropTypes.bool.isRequired,
    isNotVisible: PropTypes.bool.isRequired,
    lastColumnIndex: PropTypes.number.isRequired,
    offsetLeft: PropTypes.number.isRequired,
    onClick: PropTypes.func,
    onDoubleClick: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    pinnedColumns: PropTypes.object.isRequired,
    row: PropTypes.object.isRequired,
    rowHeight: PropTypes.oneOfType([
        PropTypes.oneOf([
            'auto'
        ]),
        PropTypes.number
    ]).isRequired,
    rowId: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]).isRequired,
    scrollbarWidth: PropTypes.number.isRequired,
    selected: PropTypes.bool.isRequired,
    showBottomBorder: PropTypes.bool.isRequired,
    visibleColumns: PropTypes.arrayOf(PropTypes.object).isRequired
} : void 0;
var MemoizedGridRow = fastMemo(GridRow);
export { MemoizedGridRow as GridRow };
